import {isAddonInvalidError} from '../../helpers/addon-error'
import {shapeQuoteOptions} from '../../helpers/addon-lines'
import {API} from '../../networking/http'
import {BookingStateParticipant} from '../../services/booking/forms/types'
import {CurrenciesEnum} from '../../types/currencies'
import {QueryMethod} from '../helpers/types'
import {PromoQuoteFields} from '../promo-codes/types'
// import {ServiceTypesEnum} from '../service/types'
import {DiscountType} from '../types'
import {CancelPolicyEnum, Equipment} from './types'

type ServiceBasedOption = {
  start: number
  end: number
  bookingType: 'SPORT' | 'TEACHER'
  areaId?: string
  sportId: string
  teacherId?: string
}

type ClassBasedOption = {
  bookingType: 'CLASS'
  slotId?: string | null
  classId: string | null
  sportType?: string
}

export type Option = {
  venueId?: string
  userId?: string
  date?: string
  splitPayment?: boolean
  participants: BookingStateParticipant[]
  useForParticipants?: boolean
  duration?: number | string
  coverEquipment?: boolean
  timeSlot?: number
} & (ClassBasedOption | ServiceBasedOption)

export type SubscriptionStatus = string

export type LimitationCheckResultType = {
  minutes?: number
  entries?: number
  credits?: number
}
export type LimitationCheckResultTypeKey = keyof LimitationCheckResultType

export type CalculatedBookingPriceParticipantsType = (BookingStateParticipant & {
  index?: number
  toPay?: boolean
  isPaid?: boolean
  discount: number
  totalPrice: number
  activityPrice: number
  totalDiscount: number
  needEquipment?: boolean
  initialDiscount: number
  equipmentsPrice: number
  activityDiscount: number
  equipmentsDiscount: number
  activityTotalPrice: number
  discountType?: DiscountType
  equipmentsTotalPrice: number
  totalInitialDiscount: number
  membershipName: string | null
  activityGroupId: string | null
  activityInitialDiscount: number
  equipmentsInitialDiscount: number
  membershipBookingId: string | null
  promoDiscount?: number
  subscriptionStatus?: SubscriptionStatus
  limitationCheckResult?: {
    used: LimitationCheckResultType
    overdraft: LimitationCheckResultType
  }
})[]

// Totals (`totalPrice`, `toPay`, `price`) are after the promo; `discount` / `totalDiscount` are
// pass coverage only and never include it (contract D364 §3).
export type CalculatedBookingPriceObject = PromoQuoteFields & {
  price: number
  toPay?: number
  activity: string
  totalPrice: number
  discount: number
  numberOfSlots: number
  activityPrice: number
  numberOfPeople: number
  equipmentPrice: number
  equipmentsPrice: number
  equipments: Equipment[]
  currency: CurrenciesEnum
  totalInitialDiscount: number
  totalDiscount: number
  activityDiscount: number
  initialDiscount?: number
  equipmentsDiscount: number
  activityTotalPrice: number
  equipmentsTotalPrice: number
  activityInitialDiscount: number
  cancelPolicy?: CancelPolicyEnum | null
  participants: CalculatedBookingPriceParticipantsType
  duration: {
    minutes: number
    hours: number
  }
}

export type CalculateBookingPriceParams = undefined
export type CalculateBookingPriceQueryParams = {
  options: Option[] | null
  promoCode?: string
}
export type CalculateBookingPriceQueryReturnType = CalculatedBookingPriceObject[]
export type CalculateBookingPriceQuery = QueryMethod<
  CalculateBookingPriceParams,
  CalculateBookingPriceQueryParams | null,
  CalculateBookingPriceQueryReturnType
>

export const calculateBookingPriceEndpoint = 'bookings/calculate/bookingPrice'

// One shared cache slot: the booking forms and payment hooks read "the current quote" with
// `enabled: false` under this exact key, so the promo code travels in the body, not the key.
export const calculateBookingPriceQueryKey = ['calculateBookingPriceQuery']

// Add-on lines go to the server as `{addonId, quantity}` and nothing else: the server prices them and its quote
// is the only figure the booking uses afterwards. Old equipment lines are sent exactly as before.
export const shapeCalculateBookingPriceQuery = (query?: CalculateBookingPriceQueryParams | null) =>
  query?.options ? {...query, options: shapeQuoteOptions(query.options)} : {...query}

export const calculateBookingPriceQuery: CalculateBookingPriceQuery = ({query, ...rest}) => ({
  queryKey: ['calculateBookingPriceQuery'],
  queryFn: () =>
    API.post<CalculateBookingPriceQueryParams | null | undefined, CalculateBookingPriceQueryReturnType>(
      calculateBookingPriceEndpoint,
      shapeCalculateBookingPriceQuery(query),
    ).then((res) => res.data),
  // A refused add-on line will be refused again: say so at once instead of trying three times.
  retry: (failureCount, error) => !isAddonInvalidError(error) && failureCount < 3,
  ...rest,
})
